import { Alert, AlertTitle, Box, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import * as React from 'react';

import { formatTs } from './components';
import type { QuotaThrottle, SandboxQuotas } from './types';

const QUOTA_NAMES: Record<string, string> = {
    bookings_total: 'bookings in total',
    bookings_per_day: 'bookings today',
    bookings_per_payload: 'bookings per request',
    requests_per_minute: 'requests per minute',
};

const Meter = ({ label, used, limit, raised }: { label: string; used: number; limit: number; raised: boolean }) => {
    const ratio = limit > 0 ? Math.min(1, used / limit) : 0;
    return (
        <Box sx={{ minWidth: 0 }} data-testid={`quota-${label}`}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                <Typography variant="body2">{label}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {used.toLocaleString()} / {limit.toLocaleString()}
                </Typography>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={ratio * 100}
                color={ratio >= 1 ? 'error' : ratio >= 0.8 ? 'warning' : 'primary'}
                sx={{ height: 6, borderRadius: 3, my: 0.5 }}
                aria-label={`${label}: ${used} of ${limit}`}
            />
            {raised && <Chip size="small" variant="outlined" label="Set by Curated Planet" />}
        </Box>
    );
};

/** "Lane paused: quota reached" (O29): the sandbox's ingress answers 429 until the quota resets. */
export const LanePausedBanner = ({ throttle }: { throttle: QuotaThrottle | null | undefined }) => (
    throttle ? (
        <Alert severity="warning" sx={{ my: 1 }} data-testid="lane-paused">
            <AlertTitle>Lane paused: quota reached</AlertTitle>
            The sandbox reached its limit of {throttle.limit.toLocaleString()} {QUOTA_NAMES[throttle.quota] || throttle.quota}
            {' '}({throttle.count.toLocaleString()}). New inbound requests are answered 429{' '}
            {throttle.reset_at
                ? `until ${formatTs(throttle.reset_at)}.`
                : 'until Curated Planet raises the limit or the sandbox is reset.'}
        </Alert>
    ) : null
);

/** The sandbox's quotas (O5, O29) and what it used. */
const QuotaPanel = ({ quotas }: { quotas: SandboxQuotas }) => {
    const raised = (key: string) => Object.prototype.hasOwnProperty.call(quotas.overrides || {}, key);
    return (
        <Stack spacing={1.5} data-testid="quota-panel">
            <Meter label="Bookings today" used={quotas.usage.bookings_per_day} limit={quotas.limits.bookings_per_day} raised={raised('bookings_per_day')} />
            <Meter label="Bookings in total" used={quotas.usage.bookings_total} limit={quotas.limits.bookings_total} raised={raised('bookings_total')} />
            <Typography variant="body2" color="text.secondary">
                At most {quotas.limits.requests_per_minute.toLocaleString()} requests per minute
                and {quotas.limits.bookings_per_payload.toLocaleString()} bookings per request. Days count in UTC.
            </Typography>
        </Stack>
    );
};

export default QuotaPanel;
